import { useState } from 'react';
import { Spinner } from 'reactstrap';

export const COLORS = {
  surface: '#fbfcfe',
  surfaceAlt: '#f3f6fb',
  border: '#d7dee9',
  borderStrong: '#b7c3d4',
  accent: '#2563eb',
  accentSoft: '#dbeafe',
  successSoft: '#e7f6ee',
  pendingSoft: '#eef3ff',
  errorSoft: '#fdebec',
  subtle: '#5f6b7c',
  muted: '#8a94a6',
  text: '#162033',
  success: '#1f7a4f',
  error: '#c2414d',
  warning: '#4f6bdc',
  monoSurface: '#0f172a',
  monoText: '#dbe7ff',
  shadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
};

export const monoFontFamily =
  '"IBM Plex Mono", "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

export const bodyFontFamily = '"Segoe UI", "Helvetica Neue", sans-serif';

const JSON_COPY_BUTTON_BORDER_COLOR = 'rgba(219, 231, 255, 0.22)';

export const styles = {
  root: {
    padding: '0',
    color: COLORS.text,
    background: 'none',
    minHeight: '100%',
    fontFamily: bodyFontFamily,
  },
  section: { marginBottom: '0.8rem', padding: '0.85rem' },
  sectionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.8rem',
    flexWrap: 'wrap',
    marginBottom: '0.75rem',
  },
  eyebrow: {
    fontSize: '0.67rem',
    fontWeight: 700,
    color: COLORS.accent,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: '0.2rem',
  },
  panelLabel: {
    fontSize: '0.67rem',
    fontWeight: 700,
    color: COLORS.subtle,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  title: {
    fontSize: '1.15rem',
    fontWeight: 800,
    lineHeight: 1.05,
    color: COLORS.text,
  },
  statsRow: {
    display: 'flex',
    gap: '0.45rem',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
  },
  statChip: {
    minWidth: '88px',
    padding: '0.42rem 0.52rem',
    borderRadius: '10px',
    backgroundColor: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
  },
  statValue: { fontSize: '0.92rem', fontWeight: 700, lineHeight: 1.1 },
  formLabelRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '0.25rem',
  },
  select: {
    borderRadius: '10px',
    border: `1px solid ${COLORS.borderStrong}`,
    backgroundColor: COLORS.surfaceAlt,
    color: COLORS.text,
    fontWeight: 600,
  },
  toolbar: { display: 'flex', gap: '0.45rem', flexWrap: 'wrap' },
  panelGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '0.7rem',
    marginBottom: '0.7rem',
  },
  stack: { display: 'grid', gap: '0.7rem', marginBottom: '0.8rem' },
  callout: {
    padding: '0.65rem 0.8rem',
    marginBottom: '0.75rem',
    backgroundColor: COLORS.pendingSoft,
    color: COLORS.warning,
    border: `1px solid ${COLORS.borderStrong}`,
    borderRadius: '10px',
    fontSize: '0.79rem',
  },
  queue: { padding: '0.8rem' },
  queueHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.7rem',
    marginBottom: '0.8rem',
  },
  queueMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.5rem',
    alignItems: 'baseline',
  },
  queueControls: { display: 'flex', flexWrap: 'wrap', gap: '0.45rem' },
  card: {
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    padding: '0.8rem',
    marginBottom: '0.8rem',
    backgroundColor: COLORS.surface,
    boxShadow: COLORS.shadow,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.8rem',
    flexWrap: 'wrap',
  },
  cardHeaderLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.55rem',
    minWidth: 0,
    flex: '1 1 280px',
  },
  cardHeaderRight: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.45rem',
    alignItems: 'flex-start',
    flex: '0 1 280px',
    justifyContent: 'flex-end',
  },
  chevronButton: {
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: COLORS.subtle,
    padding: '0.15rem 0.1rem',
    lineHeight: 1,
    fontSize: '0.9rem',
    marginTop: '0.02rem',
    flex: '0 0 auto',
  },
  badgeRow: {
    display: 'flex',
    gap: '0.35rem',
    flexWrap: 'wrap',
    marginBottom: '0.35rem',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.18rem 0.45rem',
    borderRadius: '8px',
    fontSize: '0.68rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  questionKey: {
    fontSize: '1rem',
    color: COLORS.text,
    fontWeight: 700,
    lineHeight: 1.15,
  },
  questionText: { fontSize: '0.8rem', color: COLORS.subtle, lineHeight: 1.35 },
  details: {
    borderTop: `1px solid ${COLORS.border}`,
    marginTop: '0.7rem',
    paddingTop: '0.7rem',
  },
  detailsPanel: {
    backgroundColor: COLORS.surface,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    boxShadow: COLORS.shadow,
    overflow: 'hidden',
  },
  summary: {
    cursor: 'pointer',
    padding: '0.7rem 0.8rem',
    backgroundColor: COLORS.surfaceAlt,
    listStyle: 'none',
    userSelect: 'none',
  },
  summaryLabel: {
    fontSize: '0.67rem',
    fontWeight: 700,
    color: COLORS.text,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  summaryContent: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.45rem',
    flexWrap: 'wrap',
  },
  detailsBody: { padding: '0.7rem 0.8rem' },
  toggleSummary: {
    cursor: 'pointer',
    userSelect: 'none',
    color: COLORS.subtle,
    fontSize: '0.75rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  toggleBody: { marginTop: '0.55rem' },
  textArea: {
    fontSize: '0.82rem',
    marginBottom: '0.45rem',
    resize: 'vertical',
    transition: 'none',
    borderRadius: '10px',
    border: `1px solid ${COLORS.borderStrong}`,
    backgroundColor: COLORS.surface,
    color: COLORS.text,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.35)',
  },
  jsonValidityPill: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: '999px',
    border: '1px solid transparent',
    padding: '0.12rem 0.38rem',
    fontSize: '0.58rem',
    fontWeight: 800,
    lineHeight: 1.1,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  message: { fontSize: '0.75rem', marginBottom: '0.35rem' },
  jsonRow: {
    marginBottom: '0.7rem',
    backgroundColor: COLORS.surfaceAlt,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '10px',
    padding: '0.55rem',
  },
  jsonRowPlain: {
    marginBottom: '0.7rem',
  },
  jsonBlockShell: {
    position: 'relative',
  },
  jsonCopyButton: {
    position: 'absolute',
    top: '0.45rem',
    right: '0.45rem',
    zIndex: 2,
    border: `1px solid ${JSON_COPY_BUTTON_BORDER_COLOR}`,
    backgroundColor: 'rgba(15, 23, 42, 0.88)',
    color: COLORS.monoText,
    borderRadius: '7px',
    padding: '0.25rem 0.42rem',
    fontSize: '0.68rem',
    fontWeight: 800,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.28rem',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.22)',
  },
  jsonBlock: {
    backgroundColor: COLORS.monoSurface,
    color: COLORS.monoText,
    padding: '0.65rem 4.5rem 0.65rem 0.65rem',
    borderRadius: '8px',
    maxHeight: '340px',
    overflowY: 'auto',
    overflowX: 'auto',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    fontFamily: monoFontFamily,
    userSelect: 'text',
  },
  jsonLine: { fontFamily: monoFontFamily, fontSize: '0.73rem', lineHeight: 1.5 },
  jsonSummary: {
    cursor: 'pointer',
    fontFamily: monoFontFamily,
    fontSize: '0.73rem',
    lineHeight: 1.5,
    color: COLORS.monoText,
  },
};

const BUTTON_STYLES = {
  base: {
    borderRadius: '7px',
    fontWeight: 700,
    fontSize: '0.78rem',
    lineHeight: 1.2,
    padding: '0.3rem 0.62rem',
    boxShadow: 'none',
    border: `1px solid ${COLORS.borderStrong}`,
    backgroundColor: COLORS.surface,
    color: COLORS.text,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.38rem',
    whiteSpace: 'nowrap',
  },
  primary: {
    borderColor: COLORS.accent,
    backgroundColor: COLORS.accent,
    color: '#fff',
    fontWeight: 800,
  },
  primaryOutline: {
    borderColor: COLORS.accent,
    backgroundColor: COLORS.surface,
    color: COLORS.accent,
  },
  success: {
    borderColor: '#17925f',
    backgroundColor: '#17925f',
    color: '#fff',
  },
  successOutline: {
    borderColor: '#17925f',
    backgroundColor: COLORS.surface,
    color: '#17925f',
  },
};

const isPlainObject = (value) =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const formatPrimitive = (value) => {
  if (typeof value === 'string') return `"${value}"`;
  if (value === undefined) return 'undefined';
  return String(value);
};

const getJSONPreview = (value) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    const preview = value
      .slice(0, 3)
      .map((item) => {
        if (Array.isArray(item)) return `Array(${item.length})`;
        if (isPlainObject(item)) return `Object(${Object.keys(item).length})`;
        return formatPrimitive(item);
      })
      .join(', ');
    return `[${preview}${value.length > 3 ? ', …' : ''}]`;
  }

  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    return keys.length === 0
      ? '{}'
      : `{ ${keys.slice(0, 4).join(', ')}${keys.length > 4 ? ', …' : ''} }`;
  }

  return formatPrimitive(value);
};

const formatJSONForCopy = (value) => {
  if (value === undefined) return 'undefined';

  try {
    const formatted = JSON.stringify(value, null, 2);
    return formatted === undefined ? String(value) : formatted;
  } catch (error) {
    return String(value);
  }
};

const copyTextToClipboard = async (text) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  if (typeof document === 'undefined') {
    throw new Error('Clipboard is unavailable');
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.top = '-9999px';
  document.body.appendChild(textArea);
  textArea.select();

  const didCopy = document.execCommand('copy');
  document.body.removeChild(textArea);

  if (!didCopy) {
    throw new Error('Copy command failed');
  }
};

export const StatusBadge = ({
  status,
  idleLabel,
  successLabel = 'done',
  pendingLabel = 'pending',
  errorLabel = 'error',
}) => {
  let label = idleLabel;
  let backgroundColor = COLORS.surfaceAlt;
  let color = COLORS.text;

  if (status === 'success') label = successLabel;
  if (status === 'pending') {
    label = pendingLabel;
    backgroundColor = COLORS.pendingSoft;
    color = COLORS.warning;
  }
  if (status === 'error') {
    label = errorLabel;
    backgroundColor = COLORS.errorSoft;
    color = COLORS.error;
  }

  return <span style={{ ...styles.badge, backgroundColor, color }}>{label}</span>;
};

export const StatChip = ({ label, value, tone = 'neutral' }) => {
  const backgroundColor = tone === 'accent' ? COLORS.accentSoft : COLORS.surface;
  const borderColor = tone === 'accent' ? COLORS.borderStrong : COLORS.border;
  const color =
    tone === 'accent'
      ? COLORS.accent
      : tone === 'warning'
        ? COLORS.warning
        : COLORS.text;

  return (
    <div style={{ ...styles.statChip, backgroundColor, borderColor: `1px solid ${borderColor}` }}>
      <div style={styles.panelLabel}>{label}</div>
      <div style={{ ...styles.statValue, color }}>{value}</div>
    </div>
  );
};

export const JSONValidityPill = ({ isValid }) => (
  <span
    style={{
      ...styles.jsonValidityPill,
      backgroundColor: isValid ? COLORS.successSoft : COLORS.errorSoft,
      borderColor: isValid ? 'rgba(31, 122, 79, 0.22)' : 'rgba(194, 65, 77, 0.22)',
      color: isValid ? COLORS.success : COLORS.error,
    }}
  >
    {isValid ? 'Valid JSON' : 'Invalid JSON'}
  </span>
);

export const ActionButton = ({
  children,
  variant = 'neutral',
  disabled,
  busy,
  style,
  ...props
}) => {
  const variantStyle =
    variant === 'primary'
      ? BUTTON_STYLES.primary
      : variant === 'primaryOutline'
        ? BUTTON_STYLES.primaryOutline
        : variant === 'success'
          ? BUTTON_STYLES.success
          : variant === 'successOutline'
            ? BUTTON_STYLES.successOutline
            : {};

  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        ...BUTTON_STYLES.base,
        ...variantStyle,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
      {...props}
    >
      {busy && (
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            width: '0.8rem',
            justifyContent: 'center',
          }}
        >
          <Spinner size="sm" />
        </span>
      )}
      <span>{children}</span>
    </button>
  );
};

export const Disclosure = ({
  label,
  children,
  defaultOpen = false,
  framed = false,
  emptyMessage = 'No data yet.',
  hasContent = true,
}) => (
  <details
    {...(defaultOpen ? { open: true } : {})}
    style={framed ? styles.detailsPanel : styles.details}
  >
    <summary style={framed ? styles.summary : styles.toggleSummary}>
      <span
        style={{
          ...styles.summaryContent,
          ...(framed ? styles.summaryLabel : {}),
        }}
      >
        {label}
      </span>
    </summary>
    <div style={framed ? styles.detailsBody : styles.toggleBody}>
      {hasContent ? (
        children
      ) : (
        <span style={{ color: COLORS.muted, fontSize: '0.82rem' }}>
          {emptyMessage}
        </span>
      )}
    </div>
  </details>
);

const JSONNode = ({ propertyName, value, depth = 0, defaultOpenDepth = 1 }) => {
  const indent = { paddingLeft: `${depth * 0.9}rem` };
  const isCollection = Array.isArray(value) || isPlainObject(value);

  if (!isCollection) {
    return (
      <div style={{ ...styles.jsonLine, ...indent }}>
        {propertyName != null && (
          <span style={{ color: '#93c5fd' }}>{propertyName}: </span>
        )}
        <span style={{ color: '#f8fafc' }}>{formatPrimitive(value)}</span>
      </div>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value);
  const label = Array.isArray(value) ? `Array(${entries.length})` : `Object(${entries.length})`;

  return (
    <details style={indent} {...(depth < defaultOpenDepth ? { open: true } : {})}>
      <summary style={styles.jsonSummary}>
        <span style={{ color: '#c4b5fd' }}>
          {propertyName != null ? `${propertyName}: ` : ''}
          {label}
        </span>
        <span style={{ color: '#94a3b8' }}> {getJSONPreview(value)}</span>
      </summary>
      {entries.length === 0 ? (
        <div style={{ ...styles.jsonLine, paddingLeft: '1.2rem', color: '#94a3b8' }}>
          empty
        </div>
      ) : (
        entries.map(([childPropertyName, childValue]) => (
          <JSONNode
            key={`${propertyName ?? 'root'}-${childPropertyName}`}
            propertyName={childPropertyName}
            value={childValue}
            depth={depth + 1}
            defaultOpenDepth={defaultOpenDepth}
          />
        ))
      )}
    </details>
  );
};

const JSONCopyButton = ({ value }) => {
  const [copyStatus, setCopyStatus] = useState('idle');

  const handleCopy = async () => {
    try {
      await copyTextToClipboard(formatJSONForCopy(value));
      setCopyStatus('copied');
    } catch (error) {
      setCopyStatus('failed');
    }

    window.setTimeout(() => setCopyStatus('idle'), 1400);
  };

  const label =
    copyStatus === 'copied' ? 'Copied' : copyStatus === 'failed' ? 'Failed' : 'Copy JSON';

  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={handleCopy}
      style={{
        ...styles.jsonCopyButton,
        borderColor:
          copyStatus === 'copied'
            ? 'rgba(134, 239, 172, 0.7)'
            : copyStatus === 'failed'
              ? 'rgba(252, 165, 165, 0.7)'
              : JSON_COPY_BUTTON_BORDER_COLOR,
      }}
    >
      <i className="fa fa-copy" aria-hidden="true" />
      <span>{copyStatus === 'idle' ? 'Copy' : label}</span>
    </button>
  );
};

const JSONBlockShell = ({ value, children }) => (
  <div style={styles.jsonBlockShell}>
    <JSONCopyButton value={value} />
    {children}
  </div>
);

export const JSONBlock = ({ value, defaultOpenDepth = 1 }) => {
  if (!(Array.isArray(value) || isPlainObject(value))) {
    return (
      <JSONBlockShell value={value}>
        <pre style={{ ...styles.jsonBlock, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {value == null ? 'null' : String(value)}
        </pre>
      </JSONBlockShell>
    );
  }

  const entries = Array.isArray(value)
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value);

  return (
    <JSONBlockShell value={value}>
      <div style={styles.jsonBlock}>
        {entries.length === 0 ? (
          <div style={styles.jsonLine}>{Array.isArray(value) ? '[]' : '{}'}</div>
        ) : (
          entries.map(([propertyName, propertyValue]) => (
            <JSONNode
              key={propertyName}
              propertyName={propertyName}
              value={propertyValue}
              depth={0}
              defaultOpenDepth={defaultOpenDepth}
            />
          ))
        )}
      </div>
    </JSONBlockShell>
  );
};

export const JsonRow = ({ label, value, boxed = true }) => (
  <div style={boxed ? styles.jsonRow : styles.jsonRowPlain}>
    <div style={{ ...styles.panelLabel, marginBottom: '0.35rem' }}>{label}</div>
    <JSONBlock value={value} />
  </div>
);
